import {
  CELL_FILTER,
  IEntityFilterItem,
  IAuditTrailFormRule,
  IAuditTrailFormRules,
  IInventoryCountItemItemViewAuditLog,
  WIDGET_COMPONENT,
} from '@app/shared/types';
import { WIDGET_FAMILY, TIME_FORMATS } from '@config/global-constants';
import { CellClassParams, ValueGetterParams } from 'ag-grid-community';
import { chain, eq, get, gt, isEmpty, size, toLower } from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { InventoryCountService } from './inventory-count.service';
import { toMoment } from '@shared/utils';
import { I18n } from '@app/i18n/strings-keys';

export const getAuditTrailTemp = (
  service: InventoryCountService,
  data: IInventoryCountItemItemViewAuditLog[],
  options: {
    user$: BehaviorSubject<IEntityFilterItem[]>;
    type: 'item';
    loader$: BehaviorSubject<boolean>;
  } = {
    type: 'item',
    loader$: new BehaviorSubject(true),
    user$: new BehaviorSubject([]),
  },
): IAuditTrailFormRule => {
  let minHeight = `290px`;

  const operators = chain(data)
    .map('operator')
    .unionBy('value')
    .sortBy((item) => toLower(get(item, 'text')))
    .value();

  if (eq(size(operators), 4)) {
    minHeight = `320px`;
  } else if (gt(size(operators), 4)) {
    minHeight = `350px`;
  }
  const template: IAuditTrailFormRules = {
    item: {
      value: {
        items: data,
      },
      schema: {
        type: 'object',
        properties: {
          items: {
            type: 'array',
          },
        },
      },
      formRules: {
        fields: {
          items: {
            title: I18n.Common.UpdateForm,
            widgetFamily: WIDGET_FAMILY.XUX,
            widget: WIDGET_COMPONENT.edit_grid,
            class: 'one-column-tree-grid',
            widgetData: {
              filterable: true,
              showSearch: true,
              canAdd: false,
              canEdit: false,
              multiselect: false,
              disableColumnSelector: true,
              gridMinHeight: minHeight,
              gridHeight: null,
              orderControls: false,
              showLoader: options.loader$,
              columns: [
                {
                  title: I18n.Forms.InventoryCountForm.DateAndTime,
                  filterType: CELL_FILTER.dateTime,
                  path: 'date',
                  filterParams: {
                    filterOptions: ['equals', 'inRangeDate'],
                    default: {
                      type: 'inRangeDate',
                    },
                    step: 1,
                  },
                  minWidth: 150,
                  valueGetter: (params: ValueGetterParams): string => {
                    const transactionDate = params.data.date;

                    return service.companyService.convertUTCDateWithTimeZone(
                      transactionDate,
                      true,
                      TIME_FORMATS.L_LTS,
                    );
                  },
                  defaultView: true,
                  sortable: true,
                  valueFormatter: (params): string =>
                    params.data.isNotLeaf ? params.value : '',
                  comparator: (valueA, valueB): any => {
                    if (valueA === valueB) {
                      return 0;
                    }

                    return toMoment(valueA).toISOString() >
                      toMoment(valueB).toISOString()
                      ? 1
                      : -1;
                  },
                },
                {
                  path: 'operator.value',
                  labelPath: 'operator.text',
                  title: I18n.Common.Operator,
                  minWidth: 120,
                  defaultView: true,
                  filterType: CELL_FILTER.entity,
                  filterParams: {
                    quickFilter: true,
                    values: isEmpty(data) ? options.user$ : operators,
                    filterOptions: ['containsAny'],
                  },
                  valueFormatter: (params): string =>
                    params.data.isNotLeaf ? params.value : '',
                },
                {
                  path: 'measure',
                  title: I18n.Common.MeasureName,
                  defaultView: true,
                  width: 180,
                  minWidth: 180,
                },
                {
                  path: 'count',
                  title: I18n.Common.Count,
                  defaultView: true,
                  width: 180,
                  minWidth: 180,
                },
              ],
              userGridOptions: {
                suppressHorizontalScroll: false,
                rowClassRules: {
                  'major-category-row': () => true,
                  'inventory-count-first': (params: CellClassParams): boolean =>
                    params.data.isNotLeaf,
                  'inventory-item-last': (params: CellClassParams): boolean =>
                    params.data.isLast,
                },
                overlayNoRowsTemplate: service.i18nService.instant(
                  I18n.Common.NoRowsToShow,
                ),
              },
            },
          },
        },
        defaultGroup: true,
      },
    },
  };

  return template[get(options, 'type')];
};
